import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { GridPosition } from '../types';
import { colors, fonts, shadow } from '../theme';

/* The grid is sized off a single custom property so the whole thing scales
   down together on small screens without any layout math elsewhere. */
const GridContainer = styled.div`
  --cell: 62px;
  display: inline-block;
  border: 2px solid ${colors.ink};
  background: ${colors.card};
  box-shadow: ${shadow.raised};
  line-height: 0;

  @media (max-width: 560px) {
    --cell: min(17vw, 62px);
  }
`;

const GridRow = styled.div`
  display: flex;
`;

const GridCell = styled.div<{
  $isBlocked: boolean;
  $isSelected: boolean;
  $isHighlighted: boolean;
}>`
  width: var(--cell);
  height: var(--cell);
  border: 1px solid ${colors.ruleStrong};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: ${props => (props.$isBlocked ? 'default' : 'pointer')};
  background-color: ${props =>
    props.$isBlocked
      ? colors.cellBlocked
      : props.$isSelected
      ? colors.cellSelected
      : props.$isHighlighted
      ? colors.cellWord
      : colors.card};
  transition: background-color 0.1s ease;

  /* The active square gets a crisp inner edge rather than a glow. */
  ${props =>
    props.$isSelected &&
    `box-shadow: inset 0 0 0 2px ${colors.cellSelectedEdge};`}
`;

const CellNumber = styled.div`
  position: absolute;
  top: 2px;
  left: 3px;
  font-family: ${fonts.body};
  font-size: calc(var(--cell) * 0.19);
  font-weight: 600;
  line-height: 1;
  color: ${colors.inkSoft};
  pointer-events: none;
  user-select: none;
`;

const CellInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  text-align: center;
  font-family: ${fonts.body};
  font-size: calc(var(--cell) * 0.46);
  font-weight: 600;
  line-height: 1;
  background: transparent;
  text-transform: uppercase;
  color: ${colors.ink};
  caret-color: transparent;
  cursor: pointer;

  &:focus {
    outline: none;
  }
`;

interface CrosswordGridProps {
  grid: string[][];
  width?: number;  // Grid width (columns)
  height?: number;  // Grid height (rows)
  solution?: string[][];
  onCellChange?: (row: number, col: number, value: string) => void;
  readonly?: boolean;
  showNumbers?: boolean;
  acrossClues?: { [key: string]: string };
  downClues?: { [key: string]: string };
  onClueChange?: (clueNumber: string, direction: 'across' | 'down', secondaryClueNumber?: string, secondaryDirection?: 'across' | 'down') => void;
  onStartGame?: () => void;
}

interface CrosswordGridHandle {
  navigateToClue: (clueNumber: string, clueDirection: 'across' | 'down') => void;
}

export const CrosswordGrid = React.forwardRef<CrosswordGridHandle, CrosswordGridProps>(({
  grid,
  width = 5,
  height = 5,
  solution,
  onCellChange,
  readonly = false,
  showNumbers = true,
  acrossClues = {},
  downClues = {},
  onClueChange,
  onStartGame,
}, ref) => {
  const [selectedCell, setSelectedCell] = useState<GridPosition | null>(null);
  const [direction, setDirection] = useState<'across' | 'down'>('across');
  const [highlightedCells, setHighlightedCells] = useState<Set<string>>(new Set());
  const inputRefs = useRef<{ [key: string]: HTMLInputElement }>({});

  // Expose navigateToClue method via ref
  React.useImperativeHandle(ref, () => {
    // Helper function to get cells in a clue
    const getCluePositionsHelper = (startRow: number, startCol: number, dir: 'across' | 'down'): Array<{ row: number; col: number }> => {
      if (!solution) return [];
      const positions: Array<{ row: number; col: number }> = [];

      if (dir === 'across') {
        let c = startCol;
        while (c < width && solution[startRow][c] !== '#') {
          positions.push({ row: startRow, col: c });
          c++;
        }
      } else {
        let r = startRow;
        while (r < height && solution[r][startCol] !== '#') {
          positions.push({ row: r, col: startCol });
          r++;
        }
      }

      return positions;
    };

    return {
      navigateToClue(clueNumber: string, clueDirection: 'across' | 'down') {
        if (!solution) return;

        // Find the starting cell of the clue
        let currentNumber = 1;
        for (let r = 0; r < height; r++) {
          for (let c = 0; c < width; c++) {
            if (solution[r][c] !== '#') {
              const startsAcross = c === 0 || (solution[r][c - 1] === '#');
              const startsDown = r === 0 || (solution[r - 1][c] === '#');

              // Only consider positions that start a clue in any direction
              if (startsAcross || startsDown) {
                // Check if this position starts a clue in the direction we're looking for
                if ((clueDirection === 'across' && startsAcross) || (clueDirection === 'down' && startsDown)) {
                  if (currentNumber.toString() === clueNumber) {
                    // Found the clue, now get its cells
                    const cells = getCluePositionsHelper(r, c, clueDirection);

                    // Find the first empty cell, or use the first cell
                    const targetCell = cells.find(cell => !grid[cell.row][cell.col]) || cells[0];

                    if (targetCell) {
                      setSelectedCell(targetCell);
                      setDirection(clueDirection);

                      const key = `${targetCell.row}-${targetCell.col}`;
                      setTimeout(() => {
                        if (inputRefs.current[key]) {
                          inputRefs.current[key].focus();
                        }
                      }, 0);
                    }
                    return;
                  }
                }

                // Always increment when we find a position that starts any clue
                currentNumber++;
              }
            }
          }
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solution, height, width, grid]);

  // Initialize with first valid (non-blocked) cell on mount
  // Note: selectedCell intentionally not included as we only want to run on mount
  useEffect(() => {
    if (!selectedCell && solution) {
      // Find the first non-blocked cell
      let firstValidCell: GridPosition | null = null;
      for (let r = 0; r < height; r++) {
        for (let c = 0; c < width; c++) {
          if (solution[r][c] !== '#') {
            firstValidCell = { row: r, col: c };
            break;
          }
        }
        if (firstValidCell) break;
      }

      if (firstValidCell) {
        setSelectedCell(firstValidCell);
        setDirection('across');
        // Focus the first valid cell so user can type immediately
        const key = `${firstValidCell.row}-${firstValidCell.col}`;
        setTimeout(() => {
          if (inputRefs.current[key]) {
            inputRefs.current[key].focus();
          }
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solution, height, width]);

  // Generate cell numbers based on clues
  const generateCellNumbers = () => {
    const numbers: { [key: string]: number } = {};
    let currentNumber = 1;

    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        if (solution && solution[row][col] !== '#') {
          // Check if this cell starts an across word
          const startsAcross = col === 0 || (solution[row][col - 1] === '#');
          // Check if this cell starts a down word
          const startsDown = row === 0 || (solution[row - 1][col] === '#');

          if (startsAcross || startsDown) {
            numbers[`${row}-${col}`] = currentNumber;
            currentNumber++;
          }
        }
      }
    }

    return numbers;
  };

  const cellNumbers = showNumbers ? generateCellNumbers() : {};

  // Find the clue number for a cell in a specific direction (by backtracking to word start)
  const getClueNumberForDirection = useCallback((row: number, col: number, dir: 'across' | 'down'): string | null => {
    if (!solution) return null;

    // Find the start of the word in this direction
    let startRow = row;
    let startCol = col;

    if (dir === 'across') {
      // Backtrack to the start of the across word
      while (startCol > 0 && solution[startRow][startCol - 1] !== '#') {
        startCol--;
      }
    } else {
      // Backtrack to the start of the down word
      while (startRow > 0 && solution[startRow - 1][startCol] !== '#') {
        startRow--;
      }
    }

    // Now find the clue number at this starting position
    let currentNumber = 1;
    for (let r = 0; r < height; r++) {
      for (let c = 0; c < width; c++) {
        if (solution[r][c] !== '#') {
          const startsAcross = c === 0 || (solution[r][c - 1] === '#');
          const startsDown = r === 0 || (solution[r - 1][c] === '#');
          if (startsAcross || startsDown) {
            if (r === startRow && c === startCol) {
              // Check if this position actually starts a clue in the direction we want
              if ((dir === 'across' && startsAcross) || (dir === 'down' && startsDown)) {
                return currentNumber.toString();
              }
            }
            currentNumber++;
          }
        }
      }
    }
    return null;
  }, [solution, height, width]);

  const handleCellClick = (row: number, col: number) => {
    if (readonly || (solution && solution[row][col] === '#')) return;

    if (selectedCell && selectedCell.row === row && selectedCell.col === col) {
      // Clicking same cell toggles direction (NYT behavior)
      setDirection(direction === 'across' ? 'down' : 'across');
    } else {
      // Clicking a new cell keeps the current direction (NYT behavior)
      setSelectedCell({ row, col });
    }
  };

  // Get all cells in a clue (across or down)
  const getCluePositions = (startRow: number, startCol: number, dir: 'across' | 'down'): Array<{ row: number; col: number }> => {
    if (!solution) return [];
    const positions: Array<{ row: number; col: number }> = [];

    if (dir === 'across') {
      let c = startCol;
      while (c < width && solution[startRow][c] !== '#') {
        positions.push({ row: startRow, col: c });
        c++;
      }
    } else {
      let r = startRow;
      while (r < height && solution[r][startCol] !== '#') {
        positions.push({ row: r, col: startCol });
        r++;
      }
    }

    return positions;
  };

  // Get all clues in order
  const getAllCluesInOrder = (): Array<{ row: number; col: number; direction: 'across' | 'down' }> => {
    if (!solution) return [];

    const acrossPositions: Array<{ row: number; col: number }> = [];
    const downPositions: Array<{ row: number; col: number }> = [];

    for (let r = 0; r < height; r++) {
      for (let c = 0; c < width; c++) {
        if (solution[r][c] !== '#') {
          const startsAcross = c === 0 || (solution[r][c - 1] === '#');
          const startsDown = r === 0 || (solution[r - 1][c] === '#');

          if (startsAcross) {
            acrossPositions.push({ row: r, col: c });
          }
          if (startsDown) {
            downPositions.push({ row: r, col: c });
          }
        }
      }
    }

    const allClues: Array<{ row: number; col: number; direction: 'across' | 'down' }> = [];

    // Add all across clues first
    for (const pos of acrossPositions) {
      allClues.push({ row: pos.row, col: pos.col, direction: 'across' });
    }

    // Add all down clues (a position can start both across and down clues)
    for (const pos of downPositions) {
      allClues.push({ row: pos.row, col: pos.col, direction: 'down' });
    }

    return allClues;
  };

  // Move to next/previous clue, skipping filled clues if possible
  // Note: getAllCluesInOrder and getCluePositions are not in dependencies
  // as they are internal functions whose behavior is determined by the other dependencies
  const moveToClue = useCallback((offset: number, overrideGrid?: string[][]) => {
    const allClues = getAllCluesInOrder();
    if (allClues.length === 0 || !selectedCell || !solution) return;

    // Use override grid if provided (for when we've just filled a cell but state hasn't updated)
    const gridToCheck = overrideGrid || grid;

    // Find the starting position of the current clue by backtracking from selectedCell
    let clueStartRow = selectedCell.row;
    let clueStartCol = selectedCell.col;

    if (direction === 'across') {
      // Backtrack to the start of the across clue
      while (clueStartCol > 0 && solution[clueStartRow][clueStartCol - 1] !== '#') {
        clueStartCol--;
      }
    } else {
      // Backtrack to the start of the down clue
      while (clueStartRow > 0 && solution[clueStartRow - 1][clueStartCol] !== '#') {
        clueStartRow--;
      }
    }

    // Find current clue index
    let currentIndex = -1;
    const currentKey = `${clueStartRow}-${clueStartCol}-${direction}`;
    for (let i = 0; i < allClues.length; i++) {
      const key = `${allClues[i].row}-${allClues[i].col}-${allClues[i].direction}`;
      if (key === currentKey) {
        currentIndex = i;
        break;
      }
    }

    // Find next clue with at least one unfilled cell
    let nextIndex = (currentIndex + offset + allClues.length) % allClues.length;
    let attempts = 0;
    while (attempts < allClues.length) {
      const clue = allClues[nextIndex];
      const cells = getCluePositions(clue.row, clue.col, clue.direction);
      const hasUnfilledCell = cells.some(cell => !gridToCheck[cell.row][cell.col]);

      if (hasUnfilledCell) {
        // Found a clue with unfilled cells - navigate to first empty cell (NYT behavior)
        const targetCell = cells.find(cell => !gridToCheck[cell.row][cell.col]) || cells[0];
        if (targetCell) {
          focusCell(targetCell.row, targetCell.col);
          setDirection(clue.direction);
        }
        return;
      }

      nextIndex = (nextIndex + offset + allClues.length) % allClues.length;
      attempts++;
    }

    // If all clues are filled, just move to the next one
    const nextClue = allClues[(currentIndex + offset + allClues.length) % allClues.length];
    const cells = getCluePositions(nextClue.row, nextClue.col, nextClue.direction);
    if (cells.length > 0) {
      // Always navigate to the starting cell for correct clue highlighting
      focusCell(cells[0].row, cells[0].col);
      setDirection(nextClue.direction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCell, direction, solution, grid]);

  const moveToNextClue = useCallback(() => moveToClue(1), [moveToClue]);
  const moveToPreviousClue = useCallback(() => moveToClue(-1), [moveToClue]);

  const handleKeyDown = (e: React.KeyboardEvent, row: number, col: number) => {
    if (readonly) return;

    // Handle Tab and Shift+Tab for clue navigation
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        moveToPreviousClue();
      } else {
        moveToNextClue();
      }
      return;
    }

    // Handle Enter for forward navigation
    if (e.key === 'Enter') {
      e.preventDefault();
      moveToNextClue();
      return;
    }

    if (e.key === 'Backspace') {
      if (onCellChange) {
        onCellChange(row, col, '');
      }
      moveToPreviousCell(row, col);
      e.preventDefault();
    } else if (e.key.match(/^[a-zA-Z]$/)) {
      const newValue = e.key.toUpperCase();
      if (onCellChange) {
        onCellChange(row, col, newValue);
      }

      // Check if this is the last cell in the current clue
      if (selectedCell) {
        const cells = getCluePositions(selectedCell.row, selectedCell.col, direction);
        const isLastCell =
          cells.length > 0 &&
          cells[cells.length - 1].row === row &&
          cells[cells.length - 1].col === col;

        if (isLastCell) {
          // Auto-advance to next clue with the updated grid value
          const updatedGrid = grid.map(r => [...r]);
          updatedGrid[row][col] = newValue;
          moveToClue(1, updatedGrid);
        } else {
          moveToNextCell(row, col);
        }
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft') {
      moveInDirection(row, col, 'left');
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      moveInDirection(row, col, 'right');
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      moveInDirection(row, col, 'up');
      e.preventDefault();
    } else if (e.key === 'ArrowDown') {
      moveInDirection(row, col, 'down');
      e.preventDefault();
    }
  };

  const moveToNextCell = (row: number, col: number) => {
    if (direction === 'across') {
      if (col < width - 1 && (!solution || solution[row][col + 1] !== '#')) {
        focusCell(row, col + 1);
      }
    } else {
      if (row < height - 1 && (!solution || solution[row + 1][col] !== '#')) {
        focusCell(row + 1, col);
      }
    }
  };

  const moveToPreviousCell = (row: number, col: number) => {
    if (direction === 'across') {
      if (col > 0 && (!solution || solution[row][col - 1] !== '#')) {
        focusCell(row, col - 1);
      }
    } else {
      if (row > 0 && (!solution || solution[row - 1][col] !== '#')) {
        focusCell(row - 1, col);
      }
    }
  };

  const moveInDirection = (row: number, col: number, dir: 'left' | 'right' | 'up' | 'down') => {
    const isHorizontal = dir === 'left' || dir === 'right';
    const arrowDirection = isHorizontal ? 'across' : 'down';

    // NYT behavior: perpendicular arrow toggles direction without moving
    if (arrowDirection !== direction) {
      setDirection(arrowDirection);
      return;
    }

    // Parallel arrow: move in that direction
    let newRow = row;
    let newCol = col;

    switch (dir) {
      case 'left':
        newCol = Math.max(0, col - 1);
        break;
      case 'right':
        newCol = Math.min(width - 1, col + 1);
        break;
      case 'up':
        newRow = Math.max(0, row - 1);
        break;
      case 'down':
        newRow = Math.min(height - 1, row + 1);
        break;
    }

    if (!solution || solution[newRow][newCol] !== '#') {
      focusCell(newRow, newCol);
    }
  };

  const focusCell = (row: number, col: number) => {
    const key = `${row}-${col}`;
    if (inputRefs.current[key]) {
      inputRefs.current[key].focus();
      setSelectedCell({ row, col });
    }
  };

  // Update highlighted cells when selection changes
  useEffect(() => {
    if (!selectedCell) {
      setHighlightedCells(new Set());
      return;
    }

    const highlighted = new Set<string>();
    const { row, col } = selectedCell;

    if (direction === 'across') {
      if (!solution) {
        for (let c = 0; c < width; c++) highlighted.add(`${row}-${c}`);
      } else {
        // Find word start by going left until blocked
        let startCol = col;
        while (startCol > 0 && solution[row][startCol - 1] !== '#') startCol--;
        // Highlight from word start to word end
        let c = startCol;
        while (c < width && solution[row][c] !== '#') {
          highlighted.add(`${row}-${c}`);
          c++;
        }
      }
    } else {
      if (!solution) {
        for (let r = 0; r < height; r++) highlighted.add(`${r}-${col}`);
      } else {
        // Find word start by going up until blocked
        let startRow = row;
        while (startRow > 0 && solution[startRow - 1][col] !== '#') startRow--;
        // Highlight from word start to word end
        let r = startRow;
        while (r < height && solution[r][col] !== '#') {
          highlighted.add(`${r}-${col}`);
          r++;
        }
      }
    }

    setHighlightedCells(highlighted);

    // Emit clue change with both primary and secondary clue
    const acrossClueNumber = getClueNumberForDirection(selectedCell.row, selectedCell.col, 'across');
    const downClueNumber = getClueNumberForDirection(selectedCell.row, selectedCell.col, 'down');

    if (onClueChange) {
      const primaryClueNumber = direction === 'across' ? acrossClueNumber : downClueNumber;
      const secondaryDirection = direction === 'across' ? 'down' : 'across';
      const secondaryClueNumber = direction === 'across' ? downClueNumber : acrossClueNumber;

      if (primaryClueNumber) {
        onClueChange(primaryClueNumber, direction, secondaryClueNumber || undefined, secondaryClueNumber ? secondaryDirection : undefined);
      }
    }
  }, [selectedCell, direction, solution, onClueChange, getClueNumberForDirection, width, height]);

  return (
    <GridContainer>
      {grid.map((row, rowIndex) => (
        <GridRow key={rowIndex}>
          {row.map((cell, colIndex) => {
            const isBlocked = solution ? solution[rowIndex][colIndex] === '#' : false;
            const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
            const isHighlighted = highlightedCells.has(`${rowIndex}-${colIndex}`);
            const cellNumber = cellNumbers[`${rowIndex}-${colIndex}`];

            return (
              <GridCell
                key={`${rowIndex}-${colIndex}`}
                $isBlocked={isBlocked}
                $isSelected={isSelected}
                $isHighlighted={isHighlighted && !isSelected}
                onClick={() => handleCellClick(rowIndex, colIndex)}
              >
                {cellNumber && <CellNumber>{cellNumber}</CellNumber>}
                {!isBlocked && (
                  <CellInput
                    ref={el => {
                      if (el) inputRefs.current[`${rowIndex}-${colIndex}`] = el;
                    }}
                    type="text"
                    value={cell}
                    maxLength={1}
                    readOnly={readonly}
                    onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                    onChange={() => {}} // Handled by onKeyDown
                  />
                )}
              </GridCell>
            );
          })}
        </GridRow>
      ))}
    </GridContainer>
  );
});

CrosswordGrid.displayName = 'CrosswordGrid';

export default CrosswordGrid;